import { forwardRef } from 'react'
import { Link } from 'react-router-dom'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { getInitials } from '@/lib/utils/user-helpers'
import { User, UserStatus } from '@/types'
import { cn } from '@/lib/utils/cn'
import { UserHoverCard } from '@/components/users/UserHoverCard'
import { type AvatarTextSize, getAvatarTextSizeClasses } from '@/lib/utils/text-size-utils'

/**
 * Size variants for avatar display
 */
export type AvatarSize = AvatarTextSize

/**
 * Maps size variants to avatar dimensions
 */
const sizeMap: Record<AvatarSize, string> = {
  xs: 'h-4 w-4',
  sm: 'h-6 w-6',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
  xl: 'h-16 w-16',
  xxl: 'h-24 w-24',
}

/**
 * Maps user statuses to status indicator colors
 */
const statusColorMap: Record<UserStatus, string> = {
  [UserStatus.Online]: 'bg-green-500',
  [UserStatus.OnlineReadyToChat]: 'bg-green-500',
  [UserStatus.OnlineInDeepWork]: 'bg-green-500',
  [UserStatus.Offline]: 'bg-gray-400',
  [UserStatus.AwayFromKeyboard]: 'bg-yellow-500',
  [UserStatus.DoNotDisturb]: 'bg-red-500',
  [UserStatus.Busy]: 'bg-orange-500',
}

/**
 * Maps user statuses to display labels
 */
const statusLabelMap: Record<UserStatus, string> = {
  [UserStatus.Online]: 'Online',
  [UserStatus.OnlineReadyToChat]: 'Ready to chat',
  [UserStatus.OnlineInDeepWork]: 'Deep work',
  [UserStatus.Offline]: 'Offline',
  [UserStatus.AwayFromKeyboard]: 'Away',
  [UserStatus.DoNotDisturb]: 'Do not disturb',
  [UserStatus.Busy]: 'Busy',
}

/**
 * Props for the NodeAvatarWithStatus component
 */
export interface NodeAvatarWithStatusProps {
  /**
   * The user to display avatar for
   */
  user: User
  /**
   * Size variant for the avatar (defaults to 'lg')
   */
  size?: AvatarSize
  /**
   * Optional custom className for the avatar container
   */
  className?: string
  /**
   * Optional custom className for the status indicator dot
   */
  statusIndicatorClassName?: string
  /**
   * Optional custom className for the user name text
   */
  nameClassName?: string
  /**
   * Optional custom className for the status badge
   */
  badgeClassName?: string
  /**
   * Whether to show the user name (defaults to true)
   */
  showName?: boolean
  /**
   * Whether to show the status badge (defaults to true)
   */
  showStatusBadge?: boolean
  /**
   * Whether to show a hover card with user info on hover (defaults to false)
   */
  showHoverCard?: boolean
  /**
   * Whether to wrap the component in a Link to the user profile (defaults to false)
   */
  linkToProfile?: boolean
  /**
   * Optional custom link path (defaults to `/users/${user.id}`)
   */
  linkPath?: string
  /**
   * Optional click handler (only used if linkToProfile is false)
   */
  onClick?: () => void
  /**
   * Layout direction: 'vertical' (default) or 'horizontal'
   */
  layout?: 'vertical' | 'horizontal'
}

/**
 * A component that displays a user avatar with online status indicator,
 * user name, and status badge. Based on the user display pattern from HomePage.tsx
 *
 * @example
 * ```tsx
 * // Basic usage with vertical layout (default)
 * <NodeAvatarWithStatus user={user} />
 *
 * // Horizontal layout with custom styling
 * <NodeAvatarWithStatus
 *   user={user}
 *   layout="horizontal"
 *   className="hover:ring-2 ring-primary transition-all"
 * />
 *
 * // Without name and badge, just avatar with status
 * <NodeAvatarWithStatus
 *   user={user}
 *   showName={false}
 *   showStatusBadge={false}
 * />
 *
 * // As a clickable link to user profile with hover card
 * <NodeAvatarWithStatus
 *   user={user}
 *   linkToProfile={true}
 *   showHoverCard={true}
 * />
 * ```
 */
export const NodeAvatarWithStatus = forwardRef<HTMLDivElement, NodeAvatarWithStatusProps>(
  (
    {
      user,
      size = 'lg',
      className,
      statusIndicatorClassName,
      nameClassName,
      badgeClassName,
      showName = true,
      showStatusBadge = true,
      showHoverCard = false,
      linkToProfile = false,
      linkPath,
      onClick,
      layout = 'vertical',
    },
    ref
  ) => {
    if (!user) {
      return null
    }

    const avatarSource = user.avatar
    const displayName = user.name || 'Unknown User'
    const firstName = displayName.split(' ')[0]
    const statusColor = statusColorMap[user.status] || 'bg-gray-400'
    const statusLabel = statusLabelMap[user.status] || 'Unknown'

    const avatar = (
      <Avatar className={cn(sizeMap[size], className)}>
        <AvatarImage src={avatarSource} alt={displayName} />
        <AvatarFallback className={cn(getAvatarTextSizeClasses(size))}>
          {getInitials(displayName)}
        </AvatarFallback>
      </Avatar>
    )

    const statusIndicator = (
      <div
        className={cn(
          'absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-background',
          statusColor,
          statusIndicatorClassName
        )}
      />
    )

    const nameElement = showName && (
      <p className={cn('text-sm font-medium truncate w-full text-center', nameClassName)}>
        {firstName}
      </p>
    )

    const statusBadge = showStatusBadge && (
      <Badge
        variant="secondary"
        className={cn('text-[10px] px-1.5 py-0 h-5 whitespace-nowrap', badgeClassName)}
      >
        {statusLabel}
      </Badge>
    )

    const content = (
      <div
        ref={ref}
        className={cn(
          'flex group',
          layout === 'horizontal' ? 'flex-row items-center gap-2' : 'flex-col items-center gap-1'
        )}
      >
        <div className="relative">
          {avatar}
          {statusIndicator}
        </div>
        {layout === 'horizontal' ? (
          <div className="flex flex-col gap-1 min-w-0">
            {nameElement}
            {statusBadge}
          </div>
        ) : (
          <>
            {nameElement}
            {statusBadge}
          </>
        )}
      </div>
    )

    const wrappedContent = showHoverCard ? (
      <UserHoverCard user={user}>{content}</UserHoverCard>
    ) : (
      content
    )

    if (linkToProfile) {
      const path = linkPath || `/users/${user.id}`
      return (
        <Link
          to={path}
          className={cn('transition-colors', layout === 'horizontal' ? 'flex-row' : 'flex-col')}
        >
          {wrappedContent}
        </Link>
      )
    }

    if (onClick) {
      return (
        <div className="cursor-pointer" onClick={onClick}>
          {wrappedContent}
        </div>
      )
    }

    return wrappedContent
  }
)

NodeAvatarWithStatus.displayName = 'NodeAvatarWithStatus'
